import useServiceById from "../../../../hooks/useServiceById";
import cleanUpAppId from "../../../../utils/cleanUpAppId";
import { Text, TooltipWrapper } from "@zapier/design-system";
import { ServiceIconWithFallback } from "../../../ServiceIconWithFallback";
import getAppIconUrl from "../../../../utils/getAppIconUrl";
import { Node, NodeProps } from "@xyflow/react";
import {
  areHandlesVisibleAtomFamily,
  edgesAtom,
  isNodeHoveredAtomFamily,
  isPathStep,
  nodeParentIdAtomFamily,
  shouldShowAddStepButtonAtom,
  shouldShowStepIdsAtom,
  ZapStepNodeData,
} from "@zapier/canvas-state";
import { ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP } from "@zapier/canvas-constants";
import classNames from "classnames";
import { useAtomValue } from "jotai";

import { StepId } from "../../../StepId";
import getZapStepTitle from "../../../../utils/getZapStepTitle";
import isFilterStep from "../../../../utils/isFilterStep";
import hasParent from "../../../../utils/hasParent";
import { AddStepButton } from "../../../AddStepButton";
import { Handles } from "../../..//Handles";
import { NodeHoverWrapper } from "../../../NodeHoverWrapper";
import styles from "./ZapStepNode.module.css";

export function ZapStepNode({ data, id }: NodeProps<Node<ZapStepNodeData>>) {
  const shouldShowStepIds = useAtomValue(shouldShowStepIdsAtom);
  const { stepNumber, step, isZapPublished } = data;
  const getService = useServiceById();
  const appId = step.app ?? "";
  const service = getService(appId);
  const images = service?.images;
  const serviceName = service?.name ?? cleanUpAppId(appId);
  const isPath = isPathStep(step);
  const shouldShowAddStepButton = useAtomValue(shouldShowAddStepButtonAtom);
  const edges = useAtomValue(edgesAtom);

  const parentId = useAtomValue(nodeParentIdAtomFamily(id));
  const areParentHandlesVisible = useAtomValue(
    areHandlesVisibleAtomFamily(parentId),
  );
  const isParentHovered = useAtomValue(isNodeHoveredAtomFamily(parentId));

  const size = 30;
  const stepTitle = getZapStepTitle({
    stepNumber,
    step,
    isPath,
    serviceName,
  });

  const isFilter = isFilterStep(step);
  const areHandlesVisible = useAtomValue(areHandlesVisibleAtomFamily(id));

  return (
    <>
      {shouldShowStepIds && <StepId />}
      {isZapPublished && !hasParent(edges, id) && shouldShowAddStepButton && (
        <AddStepButton
          handleType="target"
          distanceFromTop={ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP}
        />
      )}
      {isZapPublished && shouldShowAddStepButton && (
        <AddStepButton
          handleType="source"
          distanceFromTop={ZAP_STEP_NODE_HANDLE_DIST_FROM_TOP}
        />
      )}
      <NodeHoverWrapper>
        <div
          className={classNames(styles.step, {
            [styles.pathStep]: isPath,
            [styles.filterStep]: isFilter,
            [styles.isParentHovered]: isParentHovered,
          })}
        >
          <Handles
            nodeType="zapStep"
            isVisible={
              isZapPublished && (areParentHandlesVisible || areHandlesVisible)
            }
          />
          <TooltipWrapper position="north" content={serviceName}>
            {() => (
              <ServiceIconWithFallback
                hasBorder={true}
                serviceName={serviceName}
                size={size}
                src={getAppIconUrl(appId, images, size)}
              />
            )}
          </TooltipWrapper>
          <div className={styles.truncate}>
            <Text
              type="SmallPrint2"
              color={isPath ? "PrimeWhite" : "GrayWarm9"}
            >
              {`${stepNumber}. ${stepTitle}`}
            </Text>
          </div>
        </div>
      </NodeHoverWrapper>
    </>
  );
}
